/**
 * Resolves which LoanApplication row applies to a property listing.
 * Purchase + investor DSCR share one canonical file per user (see backend
 * getOrCreateForMe); this picker mirrors that by falling back to the latest
 * purchase-bucket app when there is no source/focus match.
 */

import type {
  LoanApplication,
  LoanSourceProductSummary,
} from '@/types/LoanApplicationType';
import type { Product } from '@/types/ProductType';

export function sourceProductIdFromApp(
  app: LoanApplication
): string | undefined {
  const src = app.sourceProductId;
  if (!src) return undefined;
  if (typeof src === 'string') return src;
  return (src as LoanSourceProductSummary)._id;
}

export function isPurchaseMasterFlow(flowProfile: string | undefined): boolean {
  return flowProfile === 'purchase' || flowProfile === 'investor_dscr';
}

/** Latest-updated purchase-bucket application (purchase or investor_dscr). */
export function pickPurchaseMasterApplication(
  apps: LoanApplication[]
): LoanApplication | undefined {
  const candidates = apps.filter((a) => isPurchaseMasterFlow(a.flowProfile));
  if (!candidates.length) return undefined;
  return [...candidates].sort((a, b) =>
    String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? ''))
  )[0];
}

/**
 * Picks the application shown for a product: direct source link, focus link,
 * refinance flow profile, then purchase master fallback.
 */
export function pickApplicationFor(
  product: Product,
  apps: LoanApplication[] | undefined
): LoanApplication | undefined {
  if (!apps?.length) return undefined;
  const direct = apps.find((a) => sourceProductIdFromApp(a) === product._id);
  if (direct) return direct;
  const byFocus = apps.find((a) => {
    const f = a.focusProductId;
    if (!f) return false;
    if (typeof f === 'string') return f === product._id;
    return (f as LoanSourceProductSummary)._id === product._id;
  });
  if (byFocus) return byFocus;
  if (product.transactionMode === 'refinance') {
    return apps.find((a) => a.flowProfile === 'refinance');
  }
  return pickPurchaseMasterApplication(apps);
}
